import { useEffect, useCallback } from 'react'

/**
 * Hook to handle ESC key press to close/dismiss UI elements
 *
 * @param onClose - Callback to execute when ESC is pressed
 * @param enabled - Whether the listener is active (default: true)
 *
 * @example
 * useEscClose(handleClosePopover)
 *
 * // With conditional enable
 * useEscClose(handleClose, isOpen)
 */
export function useEscClose(onClose: () => void, enabled: boolean = true): void {
  const handleEsc = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    if (!enabled) return

    window.addEventListener('keydown', handleEsc)
    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [handleEsc, enabled])
}

export default useEscClose
